import { cva, type VariantProps } from 'class-variance-authority';
import { transitionBase, uiMarker } from './shared';

/**
 * DROPDOWN MENU
 *
 * The row-action menu behind every "…" button in this product, plus the user
 * menu in the sidebar.
 *
 * Shares the popover surface treatment from overlay.recipe so a menu and a
 * popover anchored to adjacent buttons do not look like different products.
 */
export const menuContentRecipe = cva(
  [
    uiMarker,
    'z-50 min-w-[10rem] overflow-hidden rounded-md border border-border p-1',
    'bg-popover text-popover-foreground shadow-e3',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  ],
  {
    variants: {
      size: {
        sm: 'min-w-[8rem]',
        md: '',
        lg: 'min-w-[14rem]',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

export type MenuRecipeProps = VariantProps<typeof menuContentRecipe>;

export const menuItemRecipe = cva(
  [
    uiMarker,
    'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5',
    'font-sans text-body outline-none',
    'focus:bg-accent focus:text-accent-foreground',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    '[&_svg]:size-icon-sm [&_svg]:shrink-0',
    transitionBase,
  ],
  {
    variants: {
      tone: {
        default: '',
        /** Deletes and other irreversible row actions. */
        destructive:
          'text-destructive focus:bg-destructive-subtle focus:text-destructive',
      },
      /** Indents to line up with items that have a check or radio marker. */
      inset: {
        true: 'pl-8',
        false: '',
      },
    },
    defaultVariants: { tone: 'default', inset: false },
  }
);

export const menuLabelRecipe = cva([
  uiMarker,
  'px-2 py-1.5 font-sans text-caption text-muted-foreground',
]);

export const menuSeparatorRecipe = cva([uiMarker, '-mx-1 my-1 h-px bg-border']);

/** Keyboard shortcut hint, right-aligned in the item. */
export const menuShortcutRecipe = cva([
  uiMarker,
  'ml-auto font-sans text-caption tracking-widest text-muted-foreground',
]);

/** Slot holding the check/radio marker on selectable items. */
export const menuIndicatorRecipe = cva([
  uiMarker,
  'absolute left-2 flex size-3.5 items-center justify-center',
]);
